<template>
  <AuthLayout title="Login to your account">
    <form @submit.prevent="onSubmit" class="space-y-6" novalidate>
      <FormErrorSummary :errors="errorSummary" />

      <div>
        <label for="email" class="block text-sm font-medium text-gray-700">
          Email address <span class="text-red-500 ml-0.5" aria-label="required">*</span>
        </label>
        <div class="mt-1">
          <input
            v-model="email"
            id="email"
            type="email"
            autocomplete="email"
            class="appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
            :class="emailDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
            @blur="touch('email')"
          />
        </div>
        <p v-if="emailDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ emailDisplayError }}</p>
      </div>

      <div>
        <div class="flex items-center justify-between mb-1">
          <label for="password" class="block text-sm font-medium text-gray-700">
            Password <span class="text-red-500 ml-0.5" aria-label="required">*</span>
          </label>
          <router-link to="/forgot-password" class="text-sm text-primary-600 hover:text-primary-500">
            Forgot password?
          </router-link>
        </div>
        <div class="mt-1">
          <input
            v-model="password"
            id="password"
            type="password"
            autocomplete="current-password"
            class="appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
            :class="passwordDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
            @blur="touch('password')"
          />
        </div>
        <p v-if="passwordDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ passwordDisplayError }}</p>
      </div>

      <div
        v-if="error"
        class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        role="alert"
      >
        {{ error }}
      </div>

      <button
        type="submit"
        :disabled="isLoading"
        class="btn-primary w-full flex justify-center py-3 px-4 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <LoadingSpinner v-if="isLoading" type="spinner" size="sm" />
        <span v-else>Sign in</span>
      </button>
    </form>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { getRedirectPath } from '../../router/guards'
import { useFormValidation } from '../../composables/useFormValidation'
import AuthLayout from '../../layouts/AuthLayout.vue'
import LoadingSpinner from '../LoadingSpinner.vue'
import FormErrorSummary from '../FormErrorSummary.vue'
import { vm } from '../../utils/validationMessages'

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

const email = ref('')
const password = ref('')
const isLoading = ref(false)

const error = computed(() => authStore.error)

const { submitAttempted, touch, displayError, attemptSubmit } = useFormValidation()

const emailErr = computed(() => {
  if (!email.value.trim()) return vm.email.required
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) return vm.email.invalid
  return ''
})

const passwordErr = computed(() => {
  if (!password.value) return vm.password.required
  return ''
})

const emailDisplayError = computed(() => displayError('email', emailErr.value))
const passwordDisplayError = computed(() => displayError('password', passwordErr.value))

const errorSummary = computed(() => {
  if (!submitAttempted.value) return []
  const items = []
  if (emailErr.value) items.push({ field: 'email', label: 'Email address', message: emailErr.value })
  if (passwordErr.value) items.push({ field: 'password', label: 'Password', message: passwordErr.value })
  return items
})

const onSubmit = async () => {
  if (isLoading.value) return
  if (!attemptSubmit({ email: emailErr.value, password: passwordErr.value })) return

  isLoading.value = true
  try {
    await authStore.login(email.value, password.value)
    const redirectPath = getRedirectPath(route.query)
    router.push(redirectPath)
  } catch (err) {
    console.error('Login failed:', err)
  } finally {
    isLoading.value = false
  }
}
</script>
